import React, { useCallback, useEffect, useState } from "react";
import { useLang, translateFr as tfr } from "@/i18n";
import { Activity, Phone, Mail, MessageSquare, FileText, Calendar as CalIcon, ChevronRight, ArrowLeftRight, AlertCircle, Building2, RotateCcw } from "lucide-react";
import DashboardSectionCard from "@/components/dashboard/DashboardSectionCard";
import DashboardCardAction from "@/components/dashboard/DashboardCardAction";
import { cn, relativeFr } from "@/lib/utils";
import api from "@/lib/api";

const TYPE_META = {
  call: { Icon: Phone, color: "text-info bg-info/10 border-info/30" },
  email: { Icon: Mail, color: "text-info bg-info/10 border-info/30" },
  meeting: { Icon: CalIcon, color: "text-info bg-info/10 border-info/30" },
  video: { Icon: CalIcon, color: "text-info bg-info/10 border-info/30" },
  whatsapp: { Icon: MessageSquare, color: "text-success bg-success/10 border-success/30" },
  note: { Icon: FileText, color: "text-muted-foreground bg-secondary border-border" },
  PLAYER_CREATED: { Icon: Activity, color: "text-success bg-success/10 border-success/30" },
  PLAYER_STAGE_CHANGED: { Icon: ArrowLeftRight, color: "text-warning bg-warning/10 border-warning/30" },
  DOCUMENT_UPLOADED: { Icon: FileText, color: "text-info bg-info/10 border-info/30" },
  EVENT_CREATED: { Icon: CalIcon, color: "text-info bg-info/10 border-info/30" },
  CLUB_CREATED: { Icon: Building2, color: "text-muted-foreground bg-secondary border-border" },
  FOLLOW_UP_CREATED: { Icon: AlertCircle, color: "text-warning bg-warning/10 border-warning/30" },
  CONTRACT_SIGNED: { Icon: FileText, color: "text-success bg-success/10 border-success/30" },
};

/** ActivityFeedCard — unified activity feed (activity_log + interactions), cursor-paginated. */
export default function ActivityFeedCard({ playerId, onPlayerClick, refreshKey, title }) {
  const { t, tf } = useLang();
  const [items, setItems] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [error, setError] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    try {
      const r = await api.get("/dashboard/activity", { params: { limit: 5, player_id: playerId || undefined } });
      setItems(r.data.items);
      setCursor(r.data.next_cursor);
    } catch { setError(true); setItems([]); }
  }, [playerId]);

  useEffect(() => { setItems(null); load(); }, [load, refreshKey]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const r = await api.get("/dashboard/activity", { params: { limit: 15, cursor, player_id: playerId || undefined } });
      setItems((prev) => [...(prev || []), ...r.data.items]);
      setCursor(r.data.next_cursor);
    } catch { /* keep current items */ }
    finally { setLoadingMore(false); }
  };

  return (
    <DashboardSectionCard
      title={title || t("dash.card.activity")}
      icon={Activity}
      iconClass="text-muted-foreground"
      count={items?.length || 0}
      testid="card-recent-activity"
      className="lg:min-h-[520px] xl:min-h-0 xl:h-[clamp(470px,31vw,570px)]"
      footer={
        cursor && !error ? (
          <DashboardCardAction onClick={loadMore} disabled={loadingMore} testid="activity-see-all">
            {loadingMore ? t("common.loading") : t("dash.card.seeAllActivity")}
          </DashboardCardAction>
        ) : null
      }
    >
      {error ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 py-8" data-testid="activity-error">
          <div className="text-xs text-muted-foreground">{tfr("Impossible de charger l'activité.")}</div>
          <button onClick={load} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-border text-xs hover:bg-secondary/60 transition-colors" data-testid="activity-retry">
            <RotateCcw className="h-3 w-3" /> Réessayer
          </button>
        </div>
      ) : items === null ? (
        <div className="flex-1 space-y-2 py-1" aria-hidden>
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-11 rounded-lg bg-secondary/50 animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="flex-1 flex items-center justify-center py-10 text-xs text-muted-foreground" data-testid="activity-empty">
          {tf("Aucune activité récente.")}
        </div>
      ) : (
        <ol
          className={cn(
            // Always allow internal scrolling when height-constrained so rows can
            // NEVER bleed under the footer CTA (overflow guard, all viewports).
            "flex-1 min-h-0 flex flex-col gap-2 pt-3 overflow-y-auto overscroll-contain",
            items.length > 5 ? "justify-start max-h-[480px] pr-1" : "sm:justify-between"
          )}
          data-testid="activity-list"
        >
          {items.map((it) => {
            const meta = TYPE_META[it.type] || TYPE_META.note;
            const Icon = meta.Icon;
            return (
              <li key={`${it.kind}-${it.id}`} className="shrink-0">
                <button
                  onClick={() => it.player?.id && onPlayerClick?.(it.player.id)}
                  className="w-full group grid grid-cols-[42px_minmax(0,1fr)_18px] items-center gap-x-3 rounded-lg px-2 py-1.5 min-h-[54px] shrink-0 hover:bg-secondary/60 transition-colors text-left"
                  data-testid={`activity-item-${it.id}`}
                >
                  <div className={cn("shrink-0 h-[42px] w-[42px] rounded-full border flex items-center justify-center", meta.color)}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[15px] text-foreground/95 truncate">{(() => { if (!/^[A-Z_]+$/.test(it.title || "")) return it.title; const m = t(`activity.${it.title}`); return m === `activity.${it.title}` ? it.title : m; })()}</div>
                    <div className="text-xs text-muted-foreground truncate mt-1">
                      {it.actor?.type === "system" ? `${tfr("Système Athlynx")} · ` : it.actor?.name ? `${it.actor.name} · ` : ""}
                      {it.subtitle ? `${it.subtitle} · ` : ""}{relativeFr(it.at)}
                    </div>
                  </div>
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/40 group-hover:text-foreground shrink-0 transition-colors justify-self-end" />
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </DashboardSectionCard>
  );
}
